// One question of a practice, answered in place: a list of radio buttons or
// checkboxes for the choosing kinds, a sentence with inline inputs for gap
// filling and with inline drop-down lists for select options. After a check
// the question says whether it was right; the answers stay as they were typed
// until the learner tries again.

import { useId } from 'react'

import { useT } from '../../../i18n'
import practice from '../../../i18n/practice'
import { Icon } from '../../../lib/ui'
import { displayOrder, slotsOf, splitSentence } from './model'
import type { Answer, LearnerQuestion } from './model'

export function Question({
  practiceId,
  index,
  question,
  answer,
  verdict,
  missing,
  disabled,
  onAnswer,
}: {
  practiceId: string
  index: number
  question: LearnerQuestion
  answer: Answer
  /** After a check: whether it was right; undefined before. */
  verdict?: boolean
  /** Marked after a check was asked for with this question unanswered. */
  missing: boolean
  disabled: boolean
  onAnswer: (next: Answer) => void
}) {
  const t = useT(practice)
  const titleId = useId()
  const n = index + 1
  const state = verdict === undefined ? '' : verdict ? ' is-right' : ' is-wrong'

  return (
    <div
      className={`practice-q${state}${missing ? ' is-missing' : ''}`}
      role="group"
      aria-labelledby={titleId}
      data-question={question.id}
    >
      <div className="practice-q__head">
        <span id={titleId} className="practice-q__n">
          {t('questionN', { n })}
          {verdict !== undefined && <span className="visually-hidden"> — {verdict ? t('right') : t('wrong')}</span>}
        </span>
        {verdict !== undefined && (
          <span className={verdict ? 'practice-q__verdict is-right' : 'practice-q__verdict is-wrong'} aria-hidden="true">
            <Icon name={verdict ? 'check' : 'close'} size={16} />
            {verdict ? t('right') : t('wrong')}
          </span>
        )}
      </div>
      {question.kind === 'single_choice' || question.kind === 'multiple_choice' ? (
        <Choice practiceId={practiceId} question={question} answer={answer} disabled={disabled} onAnswer={onAnswer} />
      ) : (
        <Sentence question={question} answer={answer} disabled={disabled} onAnswer={onAnswer} />
      )}
      {missing && <p className="practice-q__missing">{t('unanswered')}</p>}
    </div>
  )
}

function Choice({
  practiceId,
  question,
  answer,
  disabled,
  onAnswer,
}: {
  practiceId: string
  question: LearnerQuestion
  answer: Answer
  disabled: boolean
  onAnswer: (next: Answer) => void
}) {
  const t = useT(practice)
  const single = question.kind === 'single_choice'
  const name = `practice-${practiceId}-${question.id}`
  return (
    <>
      {question.text && <p className="practice-q__text">{question.text}</p>}
      <p className="practice-q__how">{single ? t('howSingle') : t('howMultiple')}</p>
      <div className="practice-choices">
        {(question.options ?? []).map((o) => {
          const on = answer.selected.includes(o.id)
          return (
            <label key={o.id} className={on ? 'practice-choice is-on' : 'practice-choice'}>
              <input
                type={single ? 'radio' : 'checkbox'}
                name={name}
                value={o.id}
                checked={on}
                disabled={disabled}
                onChange={(e) => {
                  const selected = single
                    ? [o.id]
                    : e.target.checked
                      ? [...answer.selected, o.id]
                      : answer.selected.filter((id) => id !== o.id)
                  onAnswer({ ...answer, selected })
                }}
              />
              <span>{o.text}</span>
            </label>
          )
        })}
      </div>
    </>
  )
}

function Sentence({
  question,
  answer,
  disabled,
  onAnswer,
}: {
  question: LearnerQuestion
  answer: Answer
  disabled: boolean
  onAnswer: (next: Answer) => void
}) {
  const t = useT(practice)
  const gaps = question.kind === 'fill_blanks'
  const text = (gaps ? question.text_with_gaps : question.text_with_selects) ?? ''
  const selects = new Map((question.selects ?? []).map((s) => [s.id, s]))
  const slots = new Set(gaps ? (question.gaps ?? []).map((g) => g.id) : [...selects.keys()])
  const pieces = splitSentence(text, slots)
  // A gap or a list is named by where it stands in the sentence, as the
  // learner reads it: "Gap 2" is the second one from the left.
  const order = slotsOf(question)

  const set = (id: string, value: string) => onAnswer({ ...answer, gaps: { ...answer.gaps, [id]: value } })

  return (
    <>
      <p className="practice-q__how">{gaps ? t('howFill') : t('howSelect')}</p>
      <p className="practice-sentence">
        {pieces.map((p, i) => {
          if (p.kind === 'text') return <span key={i}>{p.text}</span>
          const n = order.indexOf(p.id) + 1
          const value = answer.gaps[p.id] ?? ''
          if (gaps) {
            return (
              <input
                key={i}
                type="text"
                className="practice-gap"
                value={value}
                disabled={disabled}
                aria-label={t('gapN', { n })}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                enterKeyHint="next"
                size={Math.min(Math.max(value.length + 2, 6), 28)}
                onChange={(e) => set(p.id, e.target.value)}
              />
            )
          }
          const select = selects.get(p.id)
          return (
            <select
              key={i}
              className="practice-dropdown"
              value={value}
              disabled={disabled}
              aria-label={t('dropdownN', { n })}
              onChange={(e) => set(p.id, e.target.value)}
            >
              <option value="">{t('choose')}</option>
              {displayOrder(select?.options ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.text}
                </option>
              ))}
            </select>
          )
        })}
      </p>
    </>
  )
}
